import * as React from 'react';
import {useEffect, useState} from 'react';

import Button from '../../../ui/Buttons/Button';
import {ExpertlyCraftedItem} from './ExpertlyCraftedItem';
import {GaItemListName} from '../../../../client/ga/ga-ecommerce.functions';
import {gaLegacyCustomEvent} from '../../../../client/ga/ga-legacy.functions';
import {Item, OrderableItem} from '../../item.class';
import {ItemToAdd} from '../../../order-items/order-items.class';
import {ListsWorkflow} from '../../../../client/lists/lists.workflow';
import {OrderItemsWorkflow} from '../../../../client/order-items/order-items.workflow';
import {OrdersService} from '../../../../client/orders/orders.service';
import {PageHeader} from '../../../ui/PageHeader/PageHeader';
import {useOrder} from '../../../lib/hooks/use-order';
import {UserStateService} from '../../../../client/users/user-state.service';
import {User} from '../../../users/user.class';
import {useService} from '../../../react/ServiceContext';
import {UpdateListItem} from '../../../lists/list.class';

interface ExpertlyCraftedProps {
    item: Item;
    user: User;
}

export const ExpertlyCrafted = ({item, user}: ExpertlyCraftedProps) => {
    const [actionsEnabled, setActionsEnabled] = useState(false);
    const [enrichedItems, setEnrichedItems] = useState<OrderableItem[]>(item.expertItems);
    const [selectedCount, setSelectedCount] = useState(0);
    const componentName = `expertly-crafted-component`;
    const gaItemListName: GaItemListName = `scheme_product4_rr`;
    const listsWorkflow: ListsWorkflow = useService(`listsWorkflow`);
    const orderItemsWorkflow: OrderItemsWorkflow = useService(`orderItemsWorkflow`);
    const ordersService: OrdersService = useService(`ordersService`);
    const userStateService: UserStateService = useService('userStateService');

    // Setup useOrder hooks
    const {order} = useOrder({
        componentName,
    });

    // Process queued actions
    useEffect(() => {
        if (userStateService.hasPendingAddToOrder(componentName)) {
            orderItemsWorkflow.addToOrderModal(userStateService.addToOrderItems, componentName, true);
        } else if (userStateService.hasPendingAddToList(componentName)) {
            listsWorkflow.addToList(
                userStateService.addToListAction,
                userStateService.addToListItems,
                componentName,
                componentName,
                userStateService.addToListItemDesc,
                userStateService.addToListItemImage,
            );
        }
        // Only run once per page load
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!item.expertItems) {
            return;
        }
        const itemsWithImages: OrderableItem[] = [];
        for (const orderableItem of item.expertItems) {
            if (orderableItem.imagePath) {
                itemsWithImages.push(orderableItem);
            }
        }
        if (itemsWithImages.length > 0) {
            const formattedItems = Item.formatItemsForDisplay(itemsWithImages, true);

            if (order && order.allLines) {
                setEnrichedItems(
                    formattedItems.map((formattedItem: OrderableItem) => {
                        for (const line of order.allLines) {
                            if (line.item === formattedItem.item) {
                                formattedItem.onOrder = true;
                            }
                        }
                        return formattedItem;
                    }),
                );
            } else {
                setEnrichedItems(formattedItems);
            }
        }
        setActionsEnabled(false);
    }, [item.expertItems, order]);

    /**
     * TBD
     */
    const addSelectedToList = () => {
        const itemsToAdd: UpdateListItem[] = [];
        enrichedItems.forEach((orderableItem) => {
            if (orderableItem.checked) {
                itemsToAdd.push({
                    item: orderableItem.item,
                    quantity: orderableItem.unitsOrdered || 1,
                });
            }
        });
        listsWorkflow.addToList(
            'add',
            itemsToAdd,
            componentName,
            `expertly crafted`,
            enrichedItems[0].description,
            enrichedItems[0].imagePath,
        );
    };

    /**
     * TBD
     */
    const addSelectedToOrder = () => {
        const itemsToAdd: ItemToAdd[] = [];
        enrichedItems.forEach((orderableItem) => {
            if (orderableItem.checked) {
                itemsToAdd.push({
                    gaEcommerce: {item_list_name: gaItemListName},
                    item: orderableItem.item,
                    unitsOrdered: orderableItem.unitsOrdered || 1,
                });
            }
        });
        orderItemsWorkflow.addToOrderModal(itemsToAdd, componentName, true);
        gaLegacyCustomEvent({eventAction: `Add All Items to Order Clicks`, eventCategory: `Ecommerce`, eventLabel: componentName});
    };

    /**
     * TBD
     * @param orderableItems
     */
    const hasChecked = (orderableItems: OrderableItem[]) => {
        let checkedItems = 0;
        orderableItems.forEach((orderableItem) => {
            if (orderableItem.checked) {
                checkedItems = checkedItems + 1;
            }
        });
        setSelectedCount(checkedItems);
        setActionsEnabled(checkedItems > 0);
    };

    /**
     * Records checkbox state
     * @param itemNum
     * @param checked
     */
    const onCheckUpdate = (itemNum: string, checked: boolean) => {
        const updatedItems = enrichedItems.map((orderableItem) => {
            if (itemNum === orderableItem.item) {
                return {...orderableItem, checked};
            }
            return orderableItem;
        });
        setEnrichedItems(updatedItems);
        hasChecked(updatedItems);
    };

    /**
     * Records item quantity
     * @param itemNum
     * @param unitsOrdered
     */
    const onQtyUpdate = (itemNum: string, unitsOrdered: number) => {
        const updatedItems = enrichedItems.map((orderableItem) => {
            if (itemNum === orderableItem.item) {
                return {...orderableItem, unitsOrdered, checked: unitsOrdered > 0};
            }
            return orderableItem;
        });
        setEnrichedItems(updatedItems);
        hasChecked(updatedItems);
    };

    /**
     * Template
     */
    if (!(enrichedItems?.length > 0)) {
        return <></>;
    }
    return (
        <>
            <PageHeader
                title="Expertly Selected Product Pairings"
                type="h4"
            />
            <div className="tw-bg-gray-100 tw-p-4">
                <div className="tw-flex tw-flex-wrap -tw-mx-2.5">
                    {enrichedItems.map((enrichedItem, index) => (
                        <ExpertlyCraftedItem
                            gaItemListName={gaItemListName}
                            item={enrichedItem}
                            key={index}
                            onCheckUpdate={onCheckUpdate}
                            onQtyUpdate={onQtyUpdate}
                            orderNumber={ordersService?.currentOrderNumber}
                            position={index + 1}
                            user={user}
                        />
                    ))}
                </div>
                <div className="tw-text-right">
                    {selectedCount > 0 && <span>{selectedCount} Item(s) Selected </span>}
                    <Button
                        e2e="EC_addToOrderButton"
                        disabled={!actionsEnabled}
                        onClick={() => {
                            addSelectedToOrder();
                        }}
                        size="lg"
                        variant="primary"
                    >
                        Add to Order
                    </Button>
                    <br />
                    <Button
                        className="disabled:tw-text-gray-600"
                        disabled={!actionsEnabled}
                        onClick={() => {
                            addSelectedToList();
                        }}
                        size="sm"
                        variant="link"
                    >
                        Add to list
                    </Button>
                </div>
            </div>
        </>
    );
};
